import * as React from "react";
import { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment/moment";
import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  ControlLabel,
  DropdownButton,
  FormControl,
  FormGroup,
  HelpBlock,
  Input,
  InputGroup,
  MenuItem
} from "components/bootstrap";
import { TimeUnitInput } from "components/common";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import HelpPanel from "components/event-definitions/common/HelpPanel";
import * as FormsUtils from "util/FormsUtils";
import AnomalyQueryInput from "security-app/components/AnomalyEvents/AnomalyQueryInput";
import ImpossibleTravelFieldCheck from "./ImpossibleTravelFieldCheck";
export const initialImpossibleTravelConfig = {
  type: "impossible_travel",
  user_field: "user_name",
  query: null,
  distance_threshold: 900,
  distance_unit: "km",
  time_threshold_ms: 36e5,
  lookback_ms: 864e5
};
const LOOKBACK_UNITS = ["HOURS", "MINUTES", "DAYS"];
const TIME_THRESHOLD_UNITS = ["HOURS", "MINUTES"];
const MS_PER_HOUR = 36e5;
const EMPTY_STRINGS = [];
const GEO_FIELD_EXAMPLE = "source_geo_coordinates";
export const formatSpeed = (distanceThreshold, distanceUnit, timeThresholdMs) => {
  if (timeThresholdMs <= 0 || distanceThreshold <= 0) return "";
  const timeHours = timeThresholdMs / MS_PER_HOUR;
  const speed = distanceThreshold / timeHours;
  const speedRounded = Math.round(speed * 10) / 10;
  if (distanceUnit === "km") {
    return `${speedRounded.toLocaleString()} km/h`;
  }
  return `${speedRounded.toLocaleString()} mph`;
};
const ImpossibleTravelEventForm = ({ eventDefinition, onChange, section = "config" }) => {
  var _a, _b, _c, _d, _e;
  const { t } = useTranslation("enterprise_security_events");
  const subtypeConfig = (_a = eventDefinition.config.config) != null ? _a : initialImpossibleTravelConfig;
  const lookback = extractDurationAndUnit(subtypeConfig.lookback_ms, LOOKBACK_UNITS);
  const [lookbackDuration, setLookbackDuration] = useState(lookback.duration);
  const [lookbackUnit, setLookbackUnit] = useState(lookback.unit);
  const timeThreshold = extractDurationAndUnit(subtypeConfig.time_threshold_ms, TIME_THRESHOLD_UNITS);
  const [timeThresholdDuration, setTimeThresholdDuration] = useState(timeThreshold.duration);
  const [timeThresholdUnit, setTimeThresholdUnit] = useState(timeThreshold.unit);
  const speedLabel = formatSpeed(
    subtypeConfig.distance_threshold,
    subtypeConfig.distance_unit,
    subtypeConfig.time_threshold_ms
  );
  const updateSubtypeConfig = (name, value) => {
    var _a2;
    const config = cloneDeep(eventDefinition.config);
    const nextSubtypeConfig = cloneDeep((_a2 = config.config) != null ? _a2 : subtypeConfig);
    nextSubtypeConfig[name] = value;
    config.config = nextSubtypeConfig;
    onChange("config", config);
  };
  const handleSubtypeInputChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    updateSubtypeConfig(name, value);
  };
  const handleLookbackChange = (nextValue, nextUnit) => {
    setLookbackDuration(nextValue);
    setLookbackUnit(nextUnit);
    const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
    updateSubtypeConfig("lookback_ms", durationInMs);
  };
  const handleTimeThresholdChange = (nextValue, nextUnit) => {
    setTimeThresholdDuration(nextValue);
    setTimeThresholdUnit(nextUnit);
    const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
    updateSubtypeConfig("time_threshold_ms", durationInMs);
  };
  const handleQueryChange = (changeEvent) => {
    updateSubtypeConfig("query", changeEvent.target.value);
    return Promise.resolve(changeEvent.target.value);
  };
  if (section === "query") {
    return /* @__PURE__ */ React.createElement(
      AnomalyQueryInput,
      {
        controlId: "impossible-travel-query",
        value: (_b = subtypeConfig.query) != null ? _b : "",
        onChange: handleQueryChange,
        streams: (_c = eventDefinition.config.streams) != null ? _c : EMPTY_STRINGS,
        eventDefinition
      }
    );
  }
  if (section === "sidebar") {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(HelpPanel, { collapsible: true, defaultExpanded: true, title: t("impossible_travel_form.help_panel_title") }, /* @__PURE__ */ React.createElement("p", null, t("impossible_travel_form.help_intro")), speedLabel && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "impossible_travel_form.help_alert",
        values: {
          distance: subtypeConfig.distance_threshold.toLocaleString(),
          unit: subtypeConfig.distance_unit === "miles" ? t("impossible_travel_units.miles") : t("impossible_travel_units.km"),
          speed: speedLabel
        },
        components: { strong: /* @__PURE__ */ React.createElement("strong", null) }
      }
    )), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "impossible_travel_form.help_distance_threshold", components: { term: /* @__PURE__ */ React.createElement("b", null) } })), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "impossible_travel_form.help_time_threshold", components: { term: /* @__PURE__ */ React.createElement("b", null) } })), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
      Trans,
      {
        t,
        i18nKey: "impossible_travel_form.help_source_fields",
        values: { geo_field: GEO_FIELD_EXAMPLE },
        components: { term: /* @__PURE__ */ React.createElement("b", null), code: /* @__PURE__ */ React.createElement("code", null) }
      }
    ))), /* @__PURE__ */ React.createElement(
      ImpossibleTravelFieldCheck,
      {
        userField: subtypeConfig.user_field,
        query: subtypeConfig.query,
        streams: (_d = eventDefinition.config.streams) != null ? _d : EMPTY_STRINGS,
        streamCategories: (_e = eventDefinition.config.stream_categories) != null ? _e : EMPTY_STRINGS,
        filters: eventDefinition.config.filters,
        lookbackMs: subtypeConfig.lookback_ms
      }
    ));
  }
  if (section !== "config") {
    return null;
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "impossible-travel-user-field",
      name: "user_field",
      label: t("impossible_travel_form.user_field"),
      type: "text",
      required: true,
      value: subtypeConfig.user_field,
      onChange: handleSubtypeInputChange,
      help: t("impossible_travel_form.user_field_help")
    }
  ), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "impossible-travel-distance-threshold" }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("impossible_travel_form.distance_threshold")), /* @__PURE__ */ React.createElement(InputGroup, { style: { display: "flex" } }, /* @__PURE__ */ React.createElement(
    FormControl,
    {
      id: "impossible-travel-distance-threshold",
      name: "distance_threshold",
      type: "number",
      min: 1,
      step: 1,
      required: true,
      value: subtypeConfig.distance_threshold,
      onChange: (e) => updateSubtypeConfig(
        "distance_threshold",
        FormsUtils.getValueFromInput(e.target)
      )
    }
  ), /* @__PURE__ */ React.createElement(
    DropdownButton,
    {
      id: "impossible-travel-distance-unit",
      title: subtypeConfig.distance_unit === "miles" ? t("impossible_travel_units.miles") : t("impossible_travel_units.kilometers")
    },
    /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        active: subtypeConfig.distance_unit === "km",
        onSelect: () => updateSubtypeConfig("distance_unit", "km")
      },
      t("impossible_travel_units.kilometers")
    ),
    /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        active: subtypeConfig.distance_unit === "miles",
        onSelect: () => updateSubtypeConfig("distance_unit", "miles")
      },
      t("impossible_travel_units.miles")
    )
  )), /* @__PURE__ */ React.createElement(HelpBlock, null, t("impossible_travel_form.distance_threshold_help"))), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "impossible-travel-time-threshold" }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: t("impossible_travel_form.time_threshold"),
      update: handleTimeThresholdChange,
      value: timeThresholdDuration,
      unit: timeThresholdUnit,
      units: TIME_THRESHOLD_UNITS,
      required: true
    }
  ), /* @__PURE__ */ React.createElement("span", { className: "help-block" }, t("impossible_travel_form.time_threshold_help"))), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "impossible-travel-lookback" }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: t("impossible_travel_form.lookback_period"),
      update: handleLookbackChange,
      value: lookbackDuration,
      unit: lookbackUnit,
      units: LOOKBACK_UNITS,
      required: true
    }
  ), /* @__PURE__ */ React.createElement("span", { className: "help-block" }, t("impossible_travel_form.lookback_period_help"))));
};
ImpossibleTravelEventForm.defaultSubtypeConfig = initialImpossibleTravelConfig;
export default ImpossibleTravelEventForm;
